import { flagEnabled } from './featureFlags';


// --- Ported from legacy OutageHelpers ---
const NBA_OUTAGE = 'outage';
const UNREACHABLE_STATUS_MAP = {
  UNREACHABLE: true,
  ISSUE_DETECTED: true,
  PROVISIONED_UNREACHABLE: true,
  UNKNOWN: true,
  OFFLINE: true,
};

const LOB_SERVICE_MAP = {
  tv: ['video'],
  video: ['video'],
  internet: ['internet'],
  hsd: ['internet'],
  voice: ['voice'],
  telephony: ['voice'],
  flex: ['internet'],
};

// Outage summary keys mapped per line of business
const OUTAGE_LOB_MAP = {
  video: ['tvOutage', 'channelOutage'],
  home: ['homeOutage'],
  internet: ['internetOutage'],
  voice: ['voiceOutage'],
  flex: ['internetOutage'],
  instantTV: [],
  internetVoice: ['voiceOutage', 'internetOutage'],
};

// ITG document IDs specific to a particular line of business
const SPECIFIC_ISSUES = {
  ITG881: 'internet',
  ITG923: 'internet',
  ITG924: 'internet',
  ITG937: 'video',
  ITG1063: 'video',
  ITG1075: 'voice',
  ITG1138: 'video',
  ITG1152: 'voice',
  ITG1153: 'home',
  ITG1194: 'video',
  ITG1219: 'home',
  ITG1220: 'home',
  ITG1342: 'internet',
  ITG1346: 'voice',
  ITG1382: 'home',
  ITG1394: 'video',
  ITG1403: 'home',
  ITG1505: 'internet',
  ITG1550: 'home',
  ITG1570: 'home',
  ITG1604: 'internet',
};

export const isUnreachableState = status => !!UNREACHABLE_STATUS_MAP[status];

const reduceOutageSummary = (summary = {}) => (
  Object.keys(summary).reduce((result, key) => {
    if (summary[key]) result.push(key);
    return result;
  }, [])
);

export const serviceHasOutage = (rawLob, summary) => {
  const lob = rawLob === 'storeFront' ? 'TV & Streaming' : rawLob;
  if (!OUTAGE_LOB_MAP[lob]) return false;
  return summary.some(outage => OUTAGE_LOB_MAP[lob].indexOf(outage) > -1);
};

const getNormalizedOutageLobs = (lobs = []) => {
  if (!Array.isArray(lobs)) return [];

  return lobs.reduce((normalizedLobs, entry) => {
    const entryLob = ((entry && entry.lob) || '').toString().toLowerCase();
    const mappedEntryLobs = LOB_SERVICE_MAP[entryLob] || [];

    const entryServices = Array.isArray(entry && entry.services) ? entry.services : [];
    const mappedServiceLobs = entryServices.reduce((serviceLobs, service) => {
      const mappedLobs = LOB_SERVICE_MAP[(service || '').toString().toLowerCase()] || [];
      return serviceLobs.concat(mappedLobs);
    }, []);

    return normalizedLobs.concat(mappedEntryLobs, mappedServiceLobs);
  }, []);
};

// Reconstructs legacy hasSubscribedLobOutage using the subscribed LOBs + outage summary
export const hasSubscribedLobOutage = (subscribedLobs = [], lobs = []) => {
  if (Array.isArray(lobs)) {
    const normalizedOutageLobs = getNormalizedOutageLobs(lobs);
    return subscribedLobs.some((lob) => {
      const mappedSubscribedLobs = LOB_SERVICE_MAP[(lob || '').toLowerCase()] || [];
      return mappedSubscribedLobs.some(mappedLob => normalizedOutageLobs.indexOf(mappedLob) > -1);
    });
  }

  const summary = reduceOutageSummary(lobs.services);
  return subscribedLobs.some(lob => serviceHasOutage(lob, summary));
};

const getMatchingLobOutage = (lobs = [], subscribedLobs = []) => {
  if (!Array.isArray(lobs)) return null;

  for (let i = 0; i < lobs.length; i += 1) {
    const entry = lobs[i] || {};
    const entryCandidates = [];
    const entryLob = (entry.lob || '').toString().toLowerCase();
    entryCandidates.push(...(LOB_SERVICE_MAP[entryLob] || []));

    const services = Array.isArray(entry.services) ? entry.services : [];
    services.forEach((service) => {
      entryCandidates.push(...(LOB_SERVICE_MAP[(service || '').toString().toLowerCase()] || []));
    });

    const matchesSubscribedLob = subscribedLobs.some((subscribedLob) => {
      const mappedSubscribedLobs = LOB_SERVICE_MAP[(subscribedLob || '').toLowerCase()] || [];
      return mappedSubscribedLobs.some(mappedLob => entryCandidates.indexOf(mappedLob) > -1);
    });

    if (matchesSubscribedLob) return entry;
  }

  return lobs[0] || null;
};

export const serviceHasLocalProblem = (rawLob = null, deviceStatus = {}) => {
  if (flagEnabled('useSsmDeviceStatus')) {
    const services = deviceStatus.services || {};
    const keys = rawLob
      ? [LOB_SERVICE_MAP[rawLob.toLowerCase()] || ''].flat()
      : Object.keys(services);
    return keys.some((service) => {
      const devices = (services[service] && services[service].devices) || [];
      return devices.some(device => isUnreachableState(device?.status?.state));
    });
  }

  const { hic: { status, services } = {} } = deviceStatus;
  if (!services) return false;
  if (!rawLob) return isUnreachableState(status);

  const lob = rawLob.toLowerCase();
  if (!LOB_SERVICE_MAP[lob]) return false;
  return LOB_SERVICE_MAP[lob].some(service => (
    isUnreachableState((services[service] || {}).status)
  ));
};

const minByPriority = (recommendations = []) => (
  recommendations.reduce((min, rec) => (
    !min || rec.priority < min.priority ? rec : min
  ), null)
);

export const parseItg = ({ recommendations = [], cxpSessionId } = {}, outageType) => {
  const {
    actions, shortDescription, longDescription, recommendationsName,
  } = minByPriority(recommendations) || {};

  const { actionIdentifier } = (
    actions && actions.find(action => action.actionType === 'ITG_1.0')
  ) || {};

  if (!actionIdentifier) return undefined;

  const docId = actionIdentifier.split(',')[0].toUpperCase();
  if (outageType && SPECIFIC_ISSUES[docId] && SPECIFIC_ISSUES[docId] !== outageType) {
    return undefined;
  }

  return {
    name: /\s*IGNORE\s*/.test(shortDescription) ? undefined : shortDescription,
    description: /\s*IGNORE\s*/.test(longDescription) ? undefined : longDescription,
    link: `https://www.xfinity.com/support/repair/guided/${cxpSessionId}/${docId}`,
    linkText: 'Let’s Fix It',
    docId,
    recommendationsName,
    outageType: SPECIFIC_ISSUES[docId] || 'internet',
  };
};

// --- Ported from legacy NbaFactory.outage handlers ---

const handleOutageLob = (outageData) => {
  const outageNBA = outageData && outageData.customerMessage;
  if (!outageNBA || !outageNBA.messages) return null;

  const { impactStyle, optInEnroll } = outageNBA;
  let color = '';
  let svgIcon;
  let icon;
  switch ((impactStyle || '').toLowerCase()) {
    case 'high impact': color = 'alert'; svgIcon = 'outage-no-circle'; break;
    case 'medium impact': color = 'warning'; svgIcon = 'outage-no-circle'; break;
    case 'low impact': color = 'notice'; svgIcon = 'icon-truck-white'; break;
    default: return null;
  }

  let link;
  let linkText;
  let linkTarget;
  const smsEnroll = outageData.smsEnroll && outageData.smsEnroll.web;
  if (smsEnroll && smsEnroll.link && optInEnroll === 'enrollAllowed') {
    ({ link } = smsEnroll);
    linkText = smsEnroll.linkText || 'Text me with updates';
    linkTarget = smsEnroll.linkTarget || '_blank';
  }

  const outageNba = {
    id: NBA_OUTAGE,
    name: outageNBA.messages.shortDescription,
    description: outageNBA.messages.detailedDescription,
    svgIcon,
    icon,
    color,
    link,
    linkText,
    linkTarget,
    isBold: 'card-bold-title',
  };

  return outageNba.name || outageNba.description ? outageNba : null;
};

const handleOutageLegacy = (outageData, subscribedLobs) => {
  if (!outageData || !outageData.lob || !hasSubscribedLobOutage(subscribedLobs, outageData.lob)) {
    return null;
  }

  const matchedLobOutage = getMatchingLobOutage(outageData.lob, subscribedLobs);
  const lobMessages = matchedLobOutage && matchedLobOutage.messages;

  const description = (lobMessages && lobMessages.detailedDescription)
    || (outageData.customerMessage && outageData.customerMessage.outage
    && outageData.customerMessage.outage.message)
    || 'There is currently an outage in your area.';
  const name = (lobMessages && lobMessages.shortDescription) || 'An outage has been reported';
  let link = 'https://www.xfinity.com/support/status/';
  let linkText = 'Status Center';
  let linkTarget = '_blank';

  if (flagEnabled('enableSmsEnrollOutageNBA')) {
    const smsEnroll = outageData.smsEnroll && outageData.smsEnroll.web;
    if (smsEnroll) {
      ({ link, linkText, linkTarget } = smsEnroll);
    }
  }

  return {
    id: NBA_OUTAGE,
    svgIcon: 'outage-no-circle',
    color: 'alert',
    name,
    description,
    link,
    linkText,
    linkTarget,
  };
};

const handleDeviceStatus = deviceStatus => (
  deviceStatus && serviceHasLocalProblem(null, deviceStatus)
    ? {
      id: NBA_OUTAGE,
      svgIcon: 'icon-exclamation',
      color: 'warning',
      name: 'We can\'t connect to your device',
      link: 'https://www.xfinity.com/support/status/',
      linkText: 'Status Center',
    }
    : null
);

const handleItg = (itg) => {
  const itgData = itg && parseItg(itg);
  if (!itgData) return null;
  return {
    id: NBA_OUTAGE,
    ...itgData,
    svgIcon: 'icon-exclamation',
    color: 'warning',
    linkTarget: '_blank',
  };
};

// Runs the outage handlers in priority order; the first match wins (parity with
// NbaFactory.outage.setData). Only one outage-family NBA is shown at a time.
export const resolveOutageNba = ({
  outageData, deviceStatus, itg, subscribedLobs = [],
} = {}) => {
  const handlers = [
    () => (flagEnabled('enableOutageLob')
      ? handleOutageLob(outageData)
      : handleOutageLegacy(outageData, subscribedLobs)),
    () => handleDeviceStatus(deviceStatus),
    () => handleItg(itg),
  ];

  for (let i = 0; i < handlers.length; i += 1) {
    const nba = handlers[i]();
    if (nba) return nba;
  }
  return null;
};
